import { useEffect, useMemo, useRef } from 'react';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { isSmartFolder } from './isSmartFolder.ts';
import { getIsSmartFolderSyncPaused, getSmartFolderLastSynced } from './SmartFolderProperties.ts';
import { shouldSkipSmartFolderAutoSync } from './smartFolderAutoSyncFreshness.ts';
import { useSmartFolderSync } from './useSmartFolderSync.ts';
import type { useSmartFolderAncestorSyncQuery } from './__generated__/useSmartFolderAncestorSyncQuery.graphql.ts';
import type { useSmartFolderAncestorSyncPropertiesQuery } from './__generated__/useSmartFolderAncestorSyncPropertiesQuery.graphql.ts';

export type SmartFolderContentPropertyNode = {
	readonly key?: string | null;
	readonly value?: unknown;
} | null;

export type SmartFolderAutoSyncPropertySources = {
	readonly ownershipProperties?: readonly SmartFolderContentPropertyNode[] | null;
	readonly smartFolderProperties?: readonly SmartFolderContentPropertyNode[] | null;
};

export type SmartFolderAutoSyncExclusionPredicate = (
	sources: SmartFolderAutoSyncPropertySources,
) => boolean;

export type UseSmartFolderAncestorSyncOptions = {
	readonly shouldExcludeAutoSync?: SmartFolderAutoSyncExclusionPredicate;
};

const SmartFolderAncestorsQuery = graphql`
	query useSmartFolderAncestorSyncQuery($contentId: ID!) {
		ptpage(id: $contentId, status: [CURRENT, DRAFT]) {
			id
			type
			subType
			nearestAncestors {
				nodes {
					id
					type
					subType
				}
			}
		}
	}
`;

const SmartFolderPropertiesQuery = graphql`
	query useSmartFolderAncestorSyncPropertiesQuery($smartFolderId: ID!) {
		singleContent(id: $smartFolderId) {
			properties(keys: ["smartFolderProperties"]) {
				nodes {
					key
					value
				}
			}
			devDocsOwnershipProperties: properties(keys: ["devdocsProperties"]) {
				nodes {
					id
					key
				}
			}
		}
	}
`;

const useSmartFolderTreeInfo = (
	contentId: string | null | undefined,
	shouldExcludeAutoSync?: SmartFolderAutoSyncExclusionPredicate,
): {
	isInSmartFolderTree: boolean;
	syncTargetId: string | null;
	smartFolderParentId: string | null;
	isSyncPaused: boolean;
	lastSynced: string | null;
	shouldSkipAutoSync: boolean;
	syncPropertiesLoading: boolean;
	loading: boolean;
} => {
	const { data, loading } = useQueryInterop<
		useSmartFolderAncestorSyncQuery['response'],
		useSmartFolderAncestorSyncQuery['variables']
	>(SmartFolderAncestorsQuery, {
		variables: { contentId: contentId ?? '' },
		skip: !contentId,
		// cache-first avoids unnecessary network requests — the page tree query
		// typically already loaded the ptpage data we need.
		fetchPolicy: 'cache-first',
	});

	const { syncTargetId, smartFolderParentId } = useMemo<{
		syncTargetId: string | null;
		smartFolderParentId: string | null;
	}>(() => {
		if (!contentId) {
			return { syncTargetId: null, smartFolderParentId: null };
		}
		const ptpage = data?.ptpage;
		if (!ptpage || ptpage.id !== contentId) {
			return { syncTargetId: null, smartFolderParentId: null };
		}

		const ancestors = ptpage?.nearestAncestors?.nodes ?? [];

		// Find the highest (root) MANAGED ancestor, if any.
		// `nearestAncestors` is ordered nearest-first, so iterate in reverse
		// to find the topmost smart folder — even when the current page is
		// itself a managed folder nested inside another managed folder, we
		// need the sibling of the OUTERMOST managed folder so the new item
		// isn't dropped inside a still-managed parent.
		let topmostManagedIndex = -1;
		for (let i = ancestors.length - 1; i >= 0; i--) {
			const ancestor = ancestors[i];
			if (ancestor && isSmartFolder(ancestor)) {
				topmostManagedIndex = i;
				break;
			}
		}

		if (topmostManagedIndex !== -1) {
			// Sibling location = the ancestor immediately above the topmost
			// managed folder, or the space root when there is nothing above.
			const parent =
				topmostManagedIndex + 1 < ancestors.length ? ancestors[topmostManagedIndex + 1] : null;
			const rootManagedAncestorId = ancestors[topmostManagedIndex]?.id ?? null;
			return {
				// FolderOverview handles sync when the current page itself is
				// a managed folder; this hook only triggers ancestor sync for
				// content nested inside one.
				syncTargetId: isSmartFolder(ptpage) ? null : rootManagedAncestorId,
				smartFolderParentId: parent?.id ?? null,
			};
		}

		// No managed ancestor. If the current page IS itself a managed folder,
		// its sibling location is simply its immediate parent (or the space
		// root when it has no page ancestor).
		if (isSmartFolder(ptpage)) {
			const parent = ancestors.length > 0 ? ancestors[0] : null;
			return {
				syncTargetId: null,
				smartFolderParentId: parent?.id ?? null,
			};
		}

		return { syncTargetId: null, smartFolderParentId: null };
	}, [contentId, data?.ptpage]);

	const {
		data: smartFolderPropertiesData,
		error: syncPropertiesError,
		loading: syncPropertiesLoading,
	} = useQueryInterop<
		useSmartFolderAncestorSyncPropertiesQuery['response'],
		useSmartFolderAncestorSyncPropertiesQuery['variables']
	>(SmartFolderPropertiesQuery, {
		variables: { smartFolderId: syncTargetId ?? '' },
		skip: !syncTargetId,
		fetchPolicy: 'cache-first',
	});
	const isSyncPaused = getIsSmartFolderSyncPaused(
		smartFolderPropertiesData?.singleContent?.properties?.nodes,
	);
	const lastSynced = getSmartFolderLastSynced(
		smartFolderPropertiesData?.singleContent?.properties?.nodes,
	);
	const hasResolvedSyncProperties = Boolean(
		!syncPropertiesLoading && !syncPropertiesError && smartFolderPropertiesData?.singleContent,
	);
	const shouldSkipAutoSync = Boolean(
		syncTargetId &&
		(!hasResolvedSyncProperties ||
			shouldExcludeAutoSync?.({
				ownershipProperties:
					smartFolderPropertiesData?.singleContent?.devDocsOwnershipProperties?.nodes,
				smartFolderProperties: smartFolderPropertiesData?.singleContent?.properties?.nodes,
			})),
	);

	// A smart folder tree is either the content itself being a smart folder
	// or having a smart folder ancestor (syncTargetId non-null).
	const isInSmartFolderTree = useMemo(
		() => (data?.ptpage ? isSmartFolder(data.ptpage) : false) || syncTargetId !== null,
		[data?.ptpage, syncTargetId],
	);

	return {
		isInSmartFolderTree,
		syncTargetId,
		smartFolderParentId,
		isSyncPaused,
		lastSynced,
		shouldSkipAutoSync,
		syncPropertiesLoading,
		loading,
	};
};

export const useIsInSmartFolderTree = (
	contentId: string | null | undefined,
): {
	isInSmartFolderTree: boolean;
	smartFolderParentId: string | null;
	loading: boolean;
} => {
	const { isInSmartFolderTree, smartFolderParentId, loading } = useSmartFolderTreeInfo(contentId);
	return { isInSmartFolderTree, smartFolderParentId, loading };
};

/**
 * For ANY piece of Confluence content (page, blog post, folder, etc.), look
 * up its ancestor chain and — if the highest ancestor is a smart (MANAGED)
 * folder — trigger a sync of that root smart folder.
 *
 * This is what powers "open a deep link to a page inside a smart folder and
 * the entire managed tree gets refreshed". The folder view itself uses
 * `useSmartFolderSync` directly; this hook covers all other content types.
 *
 * Safe to call when `contentId` is empty/falsy — in that case the query is
 * skipped and no sync is triggered.
 */
export const useSmartFolderAncestorSync = (
	contentId: string | null | undefined,
	{ shouldExcludeAutoSync }: UseSmartFolderAncestorSyncOptions = {},
): { isInSmartFolderTree: boolean } => {
	const {
		isInSmartFolderTree,
		syncTargetId,
		isSyncPaused,
		lastSynced,
		shouldSkipAutoSync,
		syncPropertiesLoading,
	} = useSmartFolderTreeInfo(contentId, shouldExcludeAutoSync);
	const { triggerSync } = useSmartFolderSync({
		triggerSurface: 'ancestorView',
		isSyncPaused,
	});
	const lastTriggeredSyncKeyRef = useRef<string | null>(null);

	// Include `contentId` so that navigating away from and back to a page
	// nested inside the same smart folder re-triggers sync even though
	// `syncTargetId` resolves to the same root folder ID both times.
	useEffect(() => {
		if (syncPropertiesLoading) {
			return;
		}

		if (!syncTargetId) {
			lastTriggeredSyncKeyRef.current = null;
			return;
		}

		if (shouldSkipAutoSync) {
			return;
		}

		const syncKey = `${contentId}:${syncTargetId}`;
		if (lastTriggeredSyncKeyRef.current === syncKey) {
			return;
		}

		lastTriggeredSyncKeyRef.current = syncKey;
		if (shouldSkipSmartFolderAutoSync({ folderId: syncTargetId, lastSynced })) {
			return;
		}
		triggerSync(syncTargetId);
	}, [contentId, lastSynced, shouldSkipAutoSync, syncPropertiesLoading, syncTargetId, triggerSync]);

	return { isInSmartFolderTree };
};
